import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  deleteCameraThunk,
  deleteCameraStreamThunk,
  postCameraStreamThunk,
  postCameraThunk,
  postDuplicateCameraThunk,
  putCameraThunk,
  postImportCameraGroupsThunk
} from '../../../reducers/fell/camera/action'
import { fetchSiteDesign, saveChangesSiteDesign } from '../../../reducers/fell/siteDesign/siteDesignActions'
import { putBulkApply } from '../../../reducers/fell/bulkapply/bulkApplyActions'
import { useAppDispatch } from '../../../reducers/hooks'
import { useNavigate } from 'react-router-dom'
import {
  BulkApplyData,
  CalculateBitrateData,
  CameraRecordingComplexCell,
  CameraRecordingDataRow,
  Resolution,
  SiteDesignSettingsData
} from '../../../lib/types'
import { ColumnDef } from '@tanstack/table-core'
import { ImportMode, InputTypes, RecordingTypes, SelectionTypes } from '../../../lib/enums'
import {
  OriginalRow,
  doubleSelectCell,
  doubleSelectCellWithAction,
  doubleTextCell,
  pdfCell,
  renderSecondaryActionCell,
  renderPrimaryActionCell,
  renderDeleteActionCell,
  singleSelectCell,
  singleTextCell,
  bitrateTextCell
} from './CameraGroupTableCells'
import Input from '../../../components/FormComponents/Input/Input'
import Select from '../../../components/FormComponents/Select/Select'
import { Row } from '@tanstack/react-table'
import { EditableTextCell } from '../../../components/Table/EditableTextCell'
import Tooltip from '../../../components/Tooltip/Tooltip'
import spritemap from '@milestone-sys/web-design-system/msds-spritemap.svg'
import { globalLoadingState } from '../../../components/Loader/Loader'
import { getCalculateBitrateThunk } from '../../../reducers/fell/tools/toolsActions'
import { downloadExcelTemplateThunk } from '../../../reducers/fell/siteDesign/siteDesignActions'
import { getComplexityLevelForScenario } from '../ImageComplexityWizard/imageComplexityScenarios'

export const columnAcessors = {
  quantity: 'quantity',
  lensType: 'lensType',
  recording: 'recording',
  hoursPerDay: 'hoursPerDay',
  retentionDays: 'retentionDays',
  codec: 'codec',
  fps: 'fps',
  motion: 'motion',
  complexity: 'complexity',
  resolution: 'resolution',
  bitrateKbit: 'bitrateKbit'
}

const {
  quantity,
  lensType,
  recording,
  hoursPerDay,
  retentionDays,
  codec,
  fps,
  motion,
  complexity,
  resolution,
  bitrateKbit
} = columnAcessors

const renderHeaderWithTooltip = (label: string, title: string, description: React.ReactNode) => (
  <div className="xsd-table-header-with-tooltip">
    <span>{label}</span>
    <Tooltip title={title} description={description} />
  </div>
)

type CameraGroupData = {
  formData
  cameraRecordings: CameraRecordingDataRow[]
  addCameraGroup: () => void
  handleDeleteCameraGroup: (rowId: number) => void
  handleModalConfirmDeletion: () => void
  handleDeleteStream: (row: OriginalRow) => void
  handleModalCancelCameraGroupDeletion: () => void
  setShowDeleteModal: (state: boolean) => void
  showDeleteModal: boolean
  addCameraStream: (recordingId: number) => void
  handleDuplicateCameraGroupTableRow: (cameraGroupId: number) => void
  updateCameraRecording: (row: Row<any>, columnId: string, value: string) => void
  cameraGroupTableColumns: ColumnDef<CameraRecordingDataRow>[]
  pdfTableColumns: ColumnDef<CameraRecordingDataRow>[]
  bulkApplyElements: any
  bulkApplyOptions: { value: string; label: string }[]
  setShowBulkApplyModal: (state: boolean) => void
  showBulkApplyModal: boolean
  applyBulkSettings: (bulkApplyData: BulkApplyData) => void
  setShowImportModal: (state: boolean) => void
  showImportModal: boolean
  handleImport: () => void
  handleImportFile: (file: File, importMode: ImportMode) => Promise<string | void>
  handleDownloadTemplate: () => void
  applyImageComplexityScenarioSelection: (streamId: number, scenarioId: string) => Promise<boolean>
  showSubRow: number
  renderSubRow: (props: { row: Row<any> }) => JSX.Element | null
}

export const CameraGroupTableInitialization = (
  siteDesignPayload,
  modifiedCameraRecordings?,
  setModifiedCameraRecordings?,
  openImageComplexityWizard?: (row: Row<any>) => void
): CameraGroupData => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [cameraRecordings, setCameraRecordings] = useState<CameraRecordingDataRow[]>(
    siteDesignPayload?.siteDesign?.cameras
  )

  const [formData, setFormData] = useState(siteDesignPayload?.formData)
  const [streamToDelete, setStreamToDelete] = useState<{ cameraGroupId: number; streamId: number } | null>(null)

  useEffect(() => {
    setFormData(siteDesignPayload?.formData)
    setCameraRecordings(() => {
      const cameraRecordings = siteDesignPayload?.siteDesign?.cameras

      if (!cameraRecordings) return

      // Create a clone and sort by ascending cameraGroupId (new ones go to bottom)
      const newCameraRecordings: CameraRecordingDataRow[] = [...cameraRecordings].sort(
        (a, b) => a.cameraGroupId - b.cameraGroupId
      )

      if (!modifiedCameraRecordings || modifiedCameraRecordings.length === 0) return newCameraRecordings

      modifiedCameraRecordings.forEach(modifiedCameraRecording => {
        // Find index of the modified recording in newCameraRecordings
        const existingIndex = newCameraRecordings.findIndex(newCameraRecording => {
          return modifiedCameraRecording.streamId === newCameraRecording.streamId
        })

        // If found, update it in place
        if (existingIndex !== -1) newCameraRecordings[existingIndex] = modifiedCameraRecording
      })

      return newCameraRecordings
    })
  }, [siteDesignPayload, modifiedCameraRecordings])

  const [cameraGroupIdToDelete, setCameraGroupIdToDelete] = useState<number | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showBulkApplyModal, setShowBulkApplyModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showSubRow, setShowSubRow] = useState<number | null>(null)

  const projectIdNumber = siteDesignPayload?.projectId
  const siteIdNumber = siteDesignPayload?.siteId
  const siteDesignIdNumber = siteDesignPayload?.siteDesignId

  const codecs = useMemo(() => formData?.codecs || {}, [formData])
  const imageComplexities = useMemo(() => formData?.imageComplexities || {}, [formData])

  const imageResolutions = useMemo<Record<string, Resolution>>(() => formData?.resolutions || {}, [formData])
  const orderedResolutions = useMemo(
    () =>
      Object.values(imageResolutions)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(res => ({
          value: res.id.toString(),
          label: res.name
        })),
    [imageResolutions]
  )

  const lensTypesOptions = useMemo(() => formData?.lensTypes || {}, [formData])
  const recordings = useMemo(() => formData?.recordings || {}, [formData])

  //Initialize the Add Camera Group event
  const addCameraGroup = async () => {
    try {
      await dispatch(
        postCameraThunk({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      )

      await dispatch(
        fetchSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      )
    } catch {
    } finally {
      setShowSubRow(null)
    }
  }

  //Initialize the Remove Camera Group Event
  const handleDeleteCameraGroup = (cameraGroupId: number) => {
    setCameraGroupIdToDelete(cameraGroupId)
    setShowDeleteModal(true)
  }

  const handleDeleteStream = (row: OriginalRow) => {
    setStreamToDelete({
      cameraGroupId: row.original.cameraGroupId,
      streamId: row.original.streamId
    })
    setShowDeleteModal(true)
  }

  const handleModalConfirmDeletion = async () => {
    try {
      setShowDeleteModal(false)

      if (streamToDelete) {
        await dispatch(
          deleteCameraStreamThunk({
            projectId: projectIdNumber,
            siteId: siteIdNumber,
            siteDesignId: siteDesignIdNumber,
            cameraGroupId: streamToDelete.cameraGroupId,
            cameraStreamId: streamToDelete.streamId
          })
        ).unwrap()

        setCameraRecordings(prev => prev.filter(r => r.streamId !== streamToDelete.streamId))

        setModifiedCameraRecordings?.(prev => (prev ? prev.filter(r => r.streamId !== streamToDelete.streamId) : null))

        setStreamToDelete(null)
      } else if (cameraGroupIdToDelete != null) {
        await dispatch(
          deleteCameraThunk({
            projectId: projectIdNumber,
            siteId: siteIdNumber,
            siteDesignId: siteDesignIdNumber,
            cameraGroupId: cameraGroupIdToDelete
          })
        ).unwrap()
        setCameraGroupIdToDelete(null)
      }

      // Your usual refetch after mutations
      await dispatch(
        fetchSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      )
    } catch {
      navigate('/error-page')
    } finally {
      setShowSubRow(null)
    }
  }

  const handleModalCancelCameraGroupDeletion = () => {
    setShowDeleteModal(false)
  }

  const addCameraStream = async (cameraGroupId: number) => {
    if (cameraGroupId < 0) return

    try {
      const parentCamera = cameraRecordings.find(c => c.cameraGroupId === cameraGroupId)

      if (parentCamera) {
        // Save the parent camera with current state before adding stream
        await dispatch(
          putCameraThunk({
            projectId: projectIdNumber,
            siteId: siteIdNumber,
            siteDesignId: siteDesignIdNumber,
            cameraGroupId: cameraGroupId,
            cameras: [parentCamera]
          })
        )
      }

      // Add new stream
      await dispatch(
        postCameraStreamThunk({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber,
          cameraGroupId: cameraGroupId
        })
      )

      // Fetch updated site design
      await dispatch(
        fetchSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      )

      // Clear the saved edits from modified recordings
      setModifiedCameraRecordings(prevRecords => {
        if (prevRecords) {
          const updatedRecords = prevRecords.filter(record => record.cameraGroupId !== cameraGroupId)
          return updatedRecords.length === 0 ? null : updatedRecords
        }
        return null
      })
    } catch (error) {
      console.error('Error updating or duplicating camera:', error)
      navigate('/error-page')
    }
  }

  const getGroupEdits = (cameraGroupId: number): CameraRecordingDataRow[] => {
    const edits = (modifiedCameraRecordings ?? []).filter(r => r.cameraGroupId === cameraGroupId)
    return edits
  }

  //Initialize the Duplicate Stream Event
  const handleDuplicateCameraGroupTableRow = async (cameraGroupId: number) => {
    if (cameraGroupId < 0) return

    try {
      const modifiedCameraGroup = getModifiedCameraGroup(cameraGroupId)
      const groupEdits = getGroupEdits(cameraGroupId)
      if (modifiedCameraGroup) {
        // Save changes first if any
        await dispatch(
          putCameraThunk({
            projectId: projectIdNumber,
            siteId: siteIdNumber,
            siteDesignId: siteDesignIdNumber,
            cameraGroupId: modifiedCameraGroup.cameraGroupId,
            cameras: groupEdits
          })
        )
      }

      // perform the duplicate
      await dispatch(
        postDuplicateCameraThunk({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber,
          cameraGroupId
        })
      )

      // Fetch updated site design
      await dispatch(
        fetchSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      )

      //if succeesslly saved the modified camera group or stream should be removed from the modified Camera group list
      if (modifiedCameraRecordings && modifiedCameraRecordings.length > 0) {
        setModifiedCameraRecordings(prevRecords => prevRecords.filter(record => record.cameraGroupId !== cameraGroupId))
      } else {
        setModifiedCameraRecordings(null)
      }
    } catch (error) {
      console.error('Error duplicating camera:', error)
      navigate('/error-page')
    } finally {
      setShowSubRow(null)
    }
  }

  const getModifiedCameraGroup = cameraGroupId => {
    if (modifiedCameraRecordings && modifiedCameraRecordings.length > 0) {
      // Find index of the modified recording in newCameraRecordings
      const existingIndex = modifiedCameraRecordings.findIndex(cameraRecording => {
        return cameraRecording.cameraGroupId === cameraGroupId
      })

      // If found, update it in place
      if (existingIndex !== -1) {
        return modifiedCameraRecordings[existingIndex]
      }
    }

    return null
  }

  const hasCameraGroupChanges = () => {
    return modifiedCameraRecordings && modifiedCameraRecordings.length > 0
  }

  const handleShowSubRow = (rowIndex: number) => {
    setShowSubRow(prev => (prev === rowIndex ? null : rowIndex))
  }

  const renderSubRow = ({ row }: { row: Row<any> }) => {
    const shouldRenderInput = showSubRow === row.index || row.original.streamName
    if (!shouldRenderInput) return null

    return (
      <tr className="msds-table__body-row">
        <td colSpan={1} className="msds-table__body-row-data">
          {!row.original.streamName && (
            <div className="row-action-container row-action-container__sub-row">
              <button
                className="row-action"
                type="button"
                onClick={() => handleShowSubRow(row.index)}
                title="Close sub row">
                <svg className="msds-icon msds-icon--sub-row">
                  <use href={`${spritemap}#right-arrow`} />
                </svg>
              </button>
            </div>
          )}
        </td>
        <td colSpan={3} className="msds-table__body-row-data">
          <EditableTextCell
            inputId={`xsd__e2e-projects-table-row-${row.id}-streamName`}
            inputType={InputTypes.Search}
            label="Camera name"
            value={row.original.streamName || ''}
            row={row}
            cameraGroupId={row.original.streamId}
            column={{ id: 'streamName' }}
            updateCellData={(rowIndex, columnId, value) => updateCameraRecording(row, columnId, value)}
            maxLength={70}
            cssClassNames="msds-table__camera-name-input"
            hideLabelOnInput={true}
          />
        </td>
        <td colSpan={9} className="msds-table__body-row-data"></td>
      </tr>
    )
  }

  const getUpdatedCameraRecording = async (streamId: number, columnId: string, value: string) => {
    let updatedCameraRecording: CameraRecordingDataRow = {
      ...cameraRecordings.find(cameraRecording => cameraRecording.streamId === streamId),
      [columnId]:
        columnId === 'continuousIsCustomBitrate' || columnId === 'motionIsCustomBitrate' ? value === 'true' : value
    }

    if (columnId === motion && updatedCameraRecording.recording === RecordingTypes.Speedup) {
      updatedCameraRecording.motion.continuous = 100 - updatedCameraRecording.motion.motion
    }

    if (columnId === bitrateKbit) {
      // When bitrate is manually changed, determine which flag to set based on recording type
      const originalBitrate = cameraRecordings.find(
        cameraRecording => cameraRecording.streamId === streamId
      )?.bitrateKbit
      const newBitrate = updatedCameraRecording.bitrateKbit

      if (updatedCameraRecording.recording === RecordingTypes.Constant) {
        updatedCameraRecording.continuousIsCustomBitrate = true
        updatedCameraRecording.motionIsCustomBitrate = false
      } else if (updatedCameraRecording.recording === RecordingTypes.Event) {
        updatedCameraRecording.motionIsCustomBitrate = true
        updatedCameraRecording.continuousIsCustomBitrate = false
      } else if (updatedCameraRecording.recording === RecordingTypes.Speedup) {
        // For speed-up, check which field was actually changed
        if (originalBitrate && newBitrate) {
          const continuousChanged = originalBitrate.continuous !== newBitrate.continuous
          const motionChanged = originalBitrate.motion !== newBitrate.motion
          if (continuousChanged) updatedCameraRecording.continuousIsCustomBitrate = true
          if (motionChanged) updatedCameraRecording.motionIsCustomBitrate = true
        }
      }
    } else if (columnId === 'continuousIsCustomBitrate' || columnId === 'motionIsCustomBitrate') {
      // When switching from user-defined to calculated, recalculate the bitrate
      if (value === 'false') {
        let bitrateValue: CameraRecordingComplexCell<number>
        if (updatedCameraRecording.recording === RecordingTypes.Speedup) {
          bitrateValue = await beforeBitrateCalculation(updatedCameraRecording, true)
        } else {
          bitrateValue = await beforeBitrateCalculation(updatedCameraRecording, false)
        }
        updatedCameraRecording.bitrateKbit = bitrateValue
      }
    } else {
      //check if the stream change is related to codec, fps, image complexity or resolution.
      //if yes then we get the bitrate calculation and update it to the current camera recoding.
      if (columnId === codec || columnId === fps || columnId === complexity || columnId === resolution) {
        //the bitrate is autmomatically calculated when one of these field has been changed.
        updatedCameraRecording.continuousIsCustomBitrate = false
        updatedCameraRecording.motionIsCustomBitrate = false

        //Checks if the the camera group is set to speedup. if so then we need to do
        //2 calculation, one of for the baseline and one for the speedup.
        let bitrateValue: CameraRecordingComplexCell<number>
        if (updatedCameraRecording.recording === RecordingTypes.Speedup) {
          bitrateValue = await beforeBitrateCalculation(updatedCameraRecording, true)
        } else {
          bitrateValue = await beforeBitrateCalculation(updatedCameraRecording, false)
        }

        //update the bitrate calculation
        updatedCameraRecording.bitrateKbit = bitrateValue
      }
    }

    return updatedCameraRecording
  }

  const beforeBitrateCalculation = async (
    updatedCameraRecording: CameraRecordingDataRow,
    codecChangedWhileSpeedup: boolean
  ) => {
    if (codecChangedWhileSpeedup) {
      // Baseline bitrate calculation
      const continuousStreamBitrateData: CalculateBitrateData = {
        complexityId: Number(updatedCameraRecording.complexity.motion),
        codecId: Number(updatedCameraRecording.codec),
        resolutionId: Number(updatedCameraRecording.resolution.motion),
        motionFps: 0,
        continuousFps: Number(updatedCameraRecording.fps.continuous)
      }
      const continuousBitrate = await getBitrateCalculation(continuousStreamBitrateData)

      // Speedup bitrate calculation
      const speedupStreamBitrateData: CalculateBitrateData = {
        complexityId: Number(updatedCameraRecording.complexity.motion),
        codecId: Number(updatedCameraRecording.codec),
        resolutionId: Number(updatedCameraRecording.resolution.motion),
        motionFps: Number(updatedCameraRecording.fps.motion),
        continuousFps: 0
      }
      const speedupBitrate = await getBitrateCalculation(speedupStreamBitrateData)

      return {
        continuous: continuousBitrate,
        motion: speedupBitrate
      }
    } else {
      // Standard bitrate calculation
      const motionFps = updatedCameraRecording.recording === 0 ? 0 : Number(updatedCameraRecording.fps.motion)
      const continuousFps = updatedCameraRecording.recording !== 0 ? 0 : Number(updatedCameraRecording.fps.continuous)

      const streamBitrateData: CalculateBitrateData = {
        complexityId:
          updatedCameraRecording.recording === 0
            ? Number(updatedCameraRecording.complexity.continuous)
            : Number(updatedCameraRecording.complexity.motion),
        codecId: Number(updatedCameraRecording.codec),
        resolutionId:
          updatedCameraRecording.recording === 0
            ? Number(updatedCameraRecording.resolution.continuous)
            : Number(updatedCameraRecording.resolution.motion),
        motionFps: motionFps,
        continuousFps: continuousFps
      }

      const bitrateValue = await getBitrateCalculation(streamBitrateData)

      return {
        continuous:
          updatedCameraRecording.recording === 0 ? bitrateValue : updatedCameraRecording.bitrateKbit.continuous,
        motion: updatedCameraRecording.recording !== 0 ? bitrateValue : updatedCameraRecording.bitrateKbit.motion
      }
    }
  }

  const getBitrateCalculation = async streamBitrateData => {
    try {
      const bitrateCalculationResponse = await dispatch(
        getCalculateBitrateThunk({
          calculateBitrateData: streamBitrateData
        })
      )
      return bitrateCalculationResponse.payload
    } catch (error) {
      console.error('Error updating bitrate:', error)
      navigate('/error-page')
    }

    return 0
  }

  const updateCameraRecording = async (row: Row<any>, columnId: string, value: string) => {
    if (row && columnId) {
      const updatedCameraRecording: CameraRecordingDataRow = await getUpdatedCameraRecording(
        row.original.streamId,
        columnId,
        value
      )
      setCameraRecordings(prevState => {
        const newCameraRecordings = [...prevState]
        const existingIndex = cameraRecordings.findIndex(
          cameraRecording => cameraRecording.id === updatedCameraRecording.id
        )

        if (existingIndex !== -1) newCameraRecordings[existingIndex] = updatedCameraRecording

        // Keep sorted by ascending cameraGroupId (new ones at bottom)
        return newCameraRecordings.sort((a, b) => a.cameraGroupId - b.cameraGroupId)
      })

      setModifiedCameraRecordings(prevState => {
        // Check if the row is already in modifiedCameraRecordings
        let newModifiedRecords = []
        if (prevState) {
          const existingIndex = prevState.findIndex(rec => rec.id === updatedCameraRecording.id)
          newModifiedRecords = [...prevState]
          if (existingIndex !== -1) {
            // Replace the existing entry
            newModifiedRecords[existingIndex] = updatedCameraRecording
          } else {
            // Add new entry
            newModifiedRecords.push(updatedCameraRecording)
          }
        } else {
          newModifiedRecords.push(updatedCameraRecording)
        }

        return newModifiedRecords
      })
    }
  }

  const normalizeComplexityLabel = (label: string) => label.toLowerCase().replace(/[^a-z0-9]/g, '')

  const getImageComplexityOptionValueByLevel = (level: string) => {
    const normalizedTarget = normalizeComplexityLabel(level)
    const entries = Object.entries(imageComplexities ?? {})
    const matchedEntry = entries.find(([, label]) => normalizeComplexityLabel(String(label)) === normalizedTarget)
    return matchedEntry?.[0] ?? null
  }

  const buildComplexityWizardValue = (
    targetRow: CameraRecordingDataRow,
    selectedComplexityValue: string
  ): CameraRecordingComplexCell<string> => {
    if (targetRow.recording === RecordingTypes.Speedup) {
      return {
        continuous: selectedComplexityValue,
        motion: selectedComplexityValue
      }
    }

    if (targetRow.recording === RecordingTypes.Constant) {
      return {
        continuous: selectedComplexityValue,
        motion: targetRow.complexity.motion
      }
    }

    return {
      continuous: targetRow.complexity.continuous,
      motion: selectedComplexityValue
    }
  }

  const applyImageComplexityScenarioSelection = async (streamId: number, scenarioId: string) => {
    const level = getComplexityLevelForScenario(scenarioId)
    if (!level) return false

    const complexityValue = getImageComplexityOptionValueByLevel(level)
    if (!complexityValue) return false

    const targetRow = cameraRecordings.find(cameraRecording => cameraRecording.streamId === streamId)
    if (!targetRow) return false

    const complexityWizardValue = buildComplexityWizardValue(targetRow, complexityValue)
    await updateCameraRecording(
      ({ original: targetRow } as unknown) as Row<any>,
      complexity,
      (complexityWizardValue as unknown) as string
    )
    return true
  }

  const updateCameraGroupLensType = async (row: Row<any>, columnId: string, value: string) => {
    if (row) {
      //gets the camera group to apply the new LensType value
      const modifiedCameraRecording = cameraRecordings.find(
        cameraRecording => cameraRecording.cameraGroupId === row.original.cameraGroupId
      )
      updateCamera(modifiedCameraRecording, columnId, value)
    }
  }

  const updateCameraStreamRecording = async (row: Row<any>, columnId: string, value: string) => {
    if (row) {
      //gets the camera group to apply the new LensType value
      const modifiedCameraRecording = cameraRecordings.find(
        cameraRecording => cameraRecording.streamId === row.original.streamId
      )
      updateCamera(modifiedCameraRecording, columnId, value)
    }
  }

  const updateCamera = async (modifiedCameraRecording, columnId: string, value: string) => {
    if (modifiedCameraRecording && columnId && value.toString()) {
      try {
        //Get the camera group where the lens type has been changed from the cameraRecordings array has it contains the latest changes.
        const updatedCameraRecording: CameraRecordingDataRow = {
          ...modifiedCameraRecording,
          [columnId]: value
        }

        if (updatedCameraRecording) {
          //We save the camera group and all the changes made to it before rendering it again with the new selected lens type
          await dispatch(
            putCameraThunk({
              projectId: projectIdNumber,
              siteId: siteIdNumber,
              siteDesignId: siteDesignIdNumber,
              cameraGroupId: updatedCameraRecording.cameraGroupId,
              cameras: Array.of<CameraRecordingDataRow>(updatedCameraRecording)
            })
          )

          await dispatch(
            fetchSiteDesign({
              projectId: projectIdNumber,
              siteId: siteIdNumber,
              siteDesignId: siteDesignIdNumber
            })
          )

          //if succeesslly saved the modified camera group or stream should be removed from the modified Camera group list
          if (modifiedCameraRecordings) {
            setModifiedCameraRecordings(prevRecords => {
              if (prevRecords) {
                const updatedRecords = prevRecords.filter(
                  record => record.cameraGroupId !== updatedCameraRecording.cameraGroupId
                )
                return updatedRecords.length === 0 ? null : updatedRecords
              }

              return null
            })
          }
        }
      } catch (error) {
        console.error('Error updating camera:', error)
        navigate('/error-page')
      }
    }
  }

  const handleImport = () => {
    setShowImportModal(true)
  }

  const handleImportFile = async (file: File, importMode: ImportMode) => {
    try {
      const isOverwrite = importMode === ImportMode.Override

      await dispatch(
        postImportCameraGroupsThunk({
          siteDesignId: siteDesignIdNumber,
          file: file,
          isOverwrite: isOverwrite
        })
      ).unwrap()

      await dispatch(
        fetchSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber
        })
      ).unwrap()

      // Clear any modified camera recordings since data has been refreshed
      setModifiedCameraRecordings(null)
      setShowImportModal(false)
    } catch (error: any) {
      const errorMessage = error?.additionalData || 'An error occurred while importing camera groups. Please try again.'
      return errorMessage
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      globalLoadingState.isForcedLoading = true

      const response = await dispatch(downloadExcelTemplateThunk())

      if (downloadExcelTemplateThunk.fulfilled.match(response)) {
        const blob = response.payload
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'Camera_Groups_Template.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        throw new Error('Failed to download template')
      }
    } catch (error) {
      console.error('Error downloading Excel template:', error)
      alert('Error downloading Excel template. Please try again.')
    } finally {
      globalLoadingState.isForcedLoading = false
    }
  }

  //We do not do async and await for this one because of the globalLoadingState otherwise the state of this object is not updated. a solution would be
  //to make a slice of use the state but as it is used only here we do not need to do it
  const applyBulkSettings = async bulkApplyData => {
    //check if there are any changes to save before applying the bulk setting
    if (hasCameraGroupChanges()) {
      //to update the bulk apply we are saving first all the changes made in the camera groups. We also don't need to pass the server settings
      //to do it. So we create a SiteDesignSettingsData variable which will contain the modified camera groups without the server setting
      //and then call the site design "savechanges" put from the API
      let siteDesignCameras: SiteDesignSettingsData = {
        cameras: [...modifiedCameraRecordings],
        serverSettings: null
      }

      //force the spinner to remain when applying the bulk value and then fetch the data again
      globalLoadingState.isForcedLoading = true

      //Call the save changes before applying the bulk setting
      dispatch(
        saveChangesSiteDesign({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber,
          siteDesignData: siteDesignCameras
        })
      )
        .then(() => {
          //Now we can safely apply the bulk apply setting change
          return dispatch(
            putBulkApply({
              projectId: projectIdNumber,
              siteId: siteIdNumber,
              siteDesignId: siteDesignIdNumber,
              bulkApplyData,
              dispatch
            })
          )
            .then(() => {
              dispatch(
                fetchSiteDesign({ projectId: projectIdNumber, siteId: siteIdNumber, siteDesignId: siteDesignIdNumber })
              )
                .then(() => {
                  //as we have saved all changes from the camera groups, we clear the modified camera group array.
                  setModifiedCameraRecordings(null)
                })
                .catch(() => navigate('/error-page'))
              globalLoadingState.isForcedLoading = false
            })
            .catch(() => navigate('/error-page'))
        })
        .catch(error => {
          console.error('Error updating camera:', error)
          globalLoadingState.isForcedLoading = false
        })
    } else {
      //as there is no camera group change, we can apply the bulk apply setting change directly without saving.
      dispatch(
        putBulkApply({
          projectId: projectIdNumber,
          siteId: siteIdNumber,
          siteDesignId: siteDesignIdNumber,
          bulkApplyData,
          dispatch
        })
      )
        .then(() =>
          dispatch(
            fetchSiteDesign({ projectId: projectIdNumber, siteId: siteIdNumber, siteDesignId: siteDesignIdNumber })
          )
        )
        .catch(() => navigate('/error-page'))
    }
  }

  // Group rows by cameraGroupId
  const rowsGroupedByGroupId: Record<number, CameraRecordingDataRow[]> =
    cameraRecordings?.reduce((groups, row) => {
      const { cameraGroupId } = row
      if (!groups[cameraGroupId]) {
        groups[cameraGroupId] = []
      }
      groups[cameraGroupId].push(row)
      return groups
    }, {} as Record<number, CameraRecordingDataRow[]>) ?? []

  // Identify child rows
  const childRows = Object.values(rowsGroupedByGroupId).flatMap(group => {
    const highestIdRow = group.reduce((maxRow, row) => (row.streamId < maxRow.streamId ? row : maxRow), group[0])
    return group.filter(row => row.streamId !== highestIdRow.streamId)
  })

  const isVisibleBasedOnMultiLensChild = useCallback(
    row => {
      const isChild = childRows.some(childRow => childRow.streamId === row.original.streamId)
      return !isChild
    },
    [childRows]
  )

  const isVisibleOnlyForChild = (row: OriginalRow) => {
    const groupRows = cameraRecordings.filter(r => r.cameraGroupId === row.original.cameraGroupId)
    const firstStreamId = Math.min(...groupRows.map(r => r.streamId))
    const isChild = row.original.streamId !== firstStreamId

    return isChild
  }

  const cameraGroupTableColumns = [
    {
      accessorKey: 'primaryActions',
      header: '',
      cell: renderSecondaryActionCell(
        isVisibleBasedOnMultiLensChild,
        handleDuplicateCameraGroupTableRow,
        addCameraStream,
        handleShowSubRow
      ),
      PdfCell: pdfCell(() => false)
    },
    {
      accessorKey: quantity,
      header: 'Cameras',
      cell: singleTextCell(InputTypes.Number, 999999, 1, updateCameraRecording, isVisibleBasedOnMultiLensChild),
      pdfCell: pdfCell(isVisibleBasedOnMultiLensChild),
      bulkApplyElement: (updateState, initialValue) => (
        <Input handleDataChange={updateState} initialValue={initialValue} maximumNumber={999999} minimumNumber={1} />
      )
    },
    {
      accessorKey: lensType,
      header: 'Lens types',
      meta: { headerLabel: 'Lens types' },
      cell: singleSelectCell(
        SelectionTypes.Number,
        isVisibleBasedOnMultiLensChild,
        lensTypesOptions,
        updateCameraGroupLensType
      ),
      pdfCell: pdfCell(isVisibleBasedOnMultiLensChild, lensTypesOptions),
      bulkApplyElement: (updateState, initialValue) => (
        <Select options={lensTypesOptions} handleDataChange={updateState} initialValue={initialValue} />
      )
    },
    {
      accessorKey: recording,
      header: () =>
        renderHeaderWithTooltip(
          'Recording',
          'Recording Type',
          <div>
            <p className="mb-2">Motion-Based Recording</p>
            <p className="mb-2">Records only when motion or events occur. Enter expected motion %.</p>
            <p className="mb-2">Constant Recording</p>
            <p className="mb-2">
              Records continuously, 24/7. Provides full coverage but requires more storage.
            </p>
            <p className="mb-2">Speedup</p>
            <p className="mb-2">
              Records at a low baseline frame rate (e.g. 1 FPS, also called keyframe recording) to reduce storage, then
              automatically increases FPS during events. A 1 FPS baseline is recommended for smooth FPS transitions.
            </p>
          </div>
        ),
      meta: { headerLabel: 'Recording' },
      cell: singleSelectCell(SelectionTypes.Number, () => true, recordings, updateCameraStreamRecording),
      pdfCell: pdfCell(() => true, recordings),
      bulkApplyElement: (updateState, initialValue) => (
        <Select options={recordings} handleDataChange={updateState} initialValue={initialValue} />
      )
    },
    {
      accessorKey: hoursPerDay,
      header: 'Operation (Hours)',
      meta: { headerLabel: 'Operation (Hours)' },
      cell: singleTextCell(InputTypes.Number, 24, 1, updateCameraRecording, () => true),
      pdfCell: pdfCell(() => true),
      bulkApplyElement: (updateState, initialValue) => (
        <Input handleDataChange={updateState} initialValue={initialValue} maximumNumber={24} minimumNumber={1} />
      )
    },
    {
      accessorKey: retentionDays,
      header: 'Days',
      meta: { headerLabel: 'Days' },
      cell: singleTextCell(InputTypes.Number, 9999, 1, updateCameraRecording, () => true),
      pdfCell: pdfCell(() => true),
      bulkApplyElement: (updateState, initialValue) => (
        <Input handleDataChange={updateState} initialValue={initialValue} maximumNumber={9999} minimumNumber={1} />
      )
    },
    {
      accessorKey: codec,
      header: () =>
        renderHeaderWithTooltip(
          'Codec',
          'Codec Type',
          <div>
            <p className="mb-2">
              H.264 is widely used, offering good quality and broad compatibility.
            </p>
            <p className="mb-2">
              H.265 provides higher compression efficiency, reducing storage and bandwidth while maintaining similar
              image quality. Requires slightly more processing power.
            </p>
          </div>
        ),
      meta: { headerLabel: 'Codec' },
      cell: singleSelectCell(SelectionTypes.Number, () => true, codecs, updateCameraRecording),
      pdfCell: pdfCell(() => true, codecs),
      bulkApplyElement: (updateState, initialValue) => (
        <Select options={codecs} handleDataChange={updateState} initialValue={initialValue} />
      )
    },
    {
      accessorKey: fps,
      header: 'FPS',
      meta: { headerLabel: 'FPS' },
      cell: doubleTextCell(InputTypes.Number, 60, 1, fps, updateCameraRecording, ['', 'Baseline', 'Speedup']),
      pdfCell: pdfCell(() => true),
      bulkApplyElement: (updateState, initialValue) => (
        <Input handleDataChange={updateState} initialValue={initialValue} maximumNumber={60} minimumNumber={1} />
      )
    },
    {
      accessorKey: motion,
      header: 'Motion/ Event(%)',
      cell: doubleTextCell(InputTypes.Number, 100, 1, motion, updateCameraRecording, null, [
        (row: OriginalRow) => row.original.recording === RecordingTypes.Constant,
        () => true,
        () => false
      ]),
      pdfCell: pdfCell(() => true),
      bulkApplyElement: (updateState, initialValue) => (
        <Input handleDataChange={updateState} initialValue={initialValue} maximumNumber={100} minimumNumber={1} />
      )
    },
    {
      accessorKey: complexity,
      header: () =>
        renderHeaderWithTooltip(
          'Image Complexity',
          'Image Complexity',
          <div>
            <p className="mb-2">
              Describes how visually busy the scene is.
            </p>
            <p className="mb-2">
              Simple scenes e.g. empty hallways have low complexity, while busy areas with movement, detail, and
              changing light have high complexity. Higher complexity increases bandwidth and storage needs.
            </p>
          </div>
        ),
      meta: { headerLabel: 'Image Complexity' },
      cell: doubleSelectCellWithAction(
        SelectionTypes.Number,
        complexity,
        imageComplexities,
        updateCameraRecording,
        openImageComplexityWizard ?? (() => null)
      ),
      pdfCell: pdfCell(() => true, imageComplexities),
      bulkApplyElement: (updateState, initialValue) => (
        <Select options={imageComplexities} handleDataChange={updateState} initialValue={initialValue} />
      )
    },
    {
      accessorKey: resolution,
      header: () =>
        renderHeaderWithTooltip(
          'Resolution',
          'Resolution Type',
          <div>
            <p className="mb-2">
              Select the video resolution (e.g. HD, Full HD, 4K). Higher resolutions provide more detail but increase
              bandwidth and storage usage.
            </p>
          </div>
        ),
      meta: { headerLabel: 'Resolution' },
      cell: doubleSelectCell(SelectionTypes.Number, resolution, orderedResolutions, updateCameraRecording),
      pdfCell: pdfCell(() => true, orderedResolutions),
      bulkApplyElement: (updateState, initialValue) => (
        <Select options={orderedResolutions} handleDataChange={updateState} initialValue={initialValue} />
      )
    },
    {
      accessorKey: bitrateKbit,
      header: () =>
        renderHeaderWithTooltip(
          'Data Rate (Kbit/Sec)',
          'Data Rate',
          <div>
            <p className="mb-2">
              If left empty, the data rate is automatically populated based on selected options. For best accuracy,
              consult the camera manufacturer, as camera model, configuration, and scene complexity can significantly
              affect actual bitrate.
            </p>
          </div>
        ),
      meta: { headerLabel: 'Data Rate (Kbit/Sec)' },
      cell: bitrateTextCell(InputTypes.Number, 26000, 1, bitrateKbit, updateCameraRecording, null, [
        (row: OriginalRow) => {
          // For non-speedup (constant/event), check the appropriate custom bitrate flag
          const isUserDefinedBitrate =
            row.original.recording === RecordingTypes.Constant
              ? row.original.continuousIsCustomBitrate
              : row.original.motionIsCustomBitrate
          return !isUserDefinedBitrate
        },
        (row: OriginalRow) => {
          // For speedup baseline, check continuous custom bitrate flag
          return !row.original.continuousIsCustomBitrate
        },
        (row: OriginalRow) => {
          // For speedup motion, check motion custom bitrate flag
          return !row.original.motionIsCustomBitrate
        }
      ]),
      pdfCell: pdfCell(() => true),
      bulkApplyElement: (updateState, initialValue) => (
        <Input handleDataChange={updateState} initialValue={initialValue} />
      )
    },
    {
      accessorKey: 'secondaryActions',
      header: '',
      cell: renderDeleteActionCell(
        isVisibleBasedOnMultiLensChild,
        isVisibleOnlyForChild,
        handleDeleteCameraGroup,
        handleDeleteStream
      ),
      PdfCell: pdfCell(() => false)
    }
  ]

  const excludedColumns: string[] = ['primaryActions', 'secondaryActions', 'childDelete']
  const getHeaderLabel = column =>
    typeof column.header === 'string' ? column.header : column.meta?.headerLabel || ''

  const pdfTableColumns = cameraGroupTableColumns
    .filter(column => typeof column.accessorKey === 'string' && !excludedColumns.includes(column.accessorKey))
    .map(column => ({
      header: getHeaderLabel(column),
      cell: column.pdfCell,
      accessorKey: column.accessorKey
    }))

  const bulkApplyElements = cameraGroupTableColumns
    .filter(column => column.bulkApplyElement && column.accessorKey)
    .reduce((elementsMap, column) => {
      elementsMap[column.accessorKey] = column.bulkApplyElement
      return elementsMap
    }, {})

  const bulkApplyOptions = cameraGroupTableColumns
    .filter(column => column.bulkApplyElement && column.accessorKey && column.accessorKey !== lensType)
    .map(column => ({ label: getHeaderLabel(column), value: column.accessorKey }))

  return {
    formData,
    cameraRecordings,
    addCameraGroup,
    updateCameraRecording,
    handleDeleteCameraGroup,
    handleDeleteStream,
    handleModalConfirmDeletion,
    handleModalCancelCameraGroupDeletion,
    setShowDeleteModal,
    showDeleteModal,
    addCameraStream,
    handleDuplicateCameraGroupTableRow,
    cameraGroupTableColumns,
    pdfTableColumns,
    bulkApplyElements,
    bulkApplyOptions,
    setShowBulkApplyModal,
    showBulkApplyModal,
    applyBulkSettings,
    setShowImportModal,
    showImportModal,
    handleImport,
    handleImportFile,
    handleDownloadTemplate,
    applyImageComplexityScenarioSelection,
    showSubRow,
    renderSubRow
  }
}
